import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { ProductionContext } from "@/contexts/production";
import type { MachineFaultDisplay } from "@/schemas";
import { CVPRangeErrorsAndWarnings } from "@/components/errors-and-warnings/errors-and-warnings-cvp-range";
import { EmErrorsAndWarnings } from "@/components/errors-and-warnings/errors-and-warnings-em";
import { FusionErrorsAndWarnings } from "@/components/errors-and-warnings/errors-and-warnings-fusion";
import { X4ErrorsAndWarnings } from "@/components/errors-and-warnings/errors-and-warnings-x4";
import { X5ErrorsAndWarnings } from "@/components/errors-and-warnings/errors-and-warnings-x5";
import { X6ErrorsAndWarnings } from "@/components/errors-and-warnings/errors-and-warnings-x6";
import { ZebraErrorsAndWarnings } from "@/components/errors-and-warnings/errors-and-warnings-zebra";
import { X7ErrorsAndWarnings } from "@/components/errors-and-warnings/errors-and-warnings-x7";

export interface ErrorsAndWarningsContextValue {
	readonly isOpen: boolean;
	readonly openModal: () => void;
	readonly closeModal: () => void;
	readonly faults: MachineFaultDisplay[];
}

const initialValue: ErrorsAndWarningsContextValue = {
	isOpen: false,
	openModal: () => {},
	closeModal: () => {},
	faults: [],
};

export const ErrorsAndWarningsContext = createContext<ErrorsAndWarningsContextValue>(initialValue);

interface ErrorsAndWarningsProviderProps {
	readonly children: ReactNode;
}

export const ErrorsAndWarningsProvider = ({ children }: ErrorsAndWarningsProviderProps) => {
	const { currentMachineGroup } = useContext(ProductionContext);
	const [isOpen, setIsOpen] = useState(false);
	const [errors, setErrors] = useState<MachineFaultDisplay[]>([]);
	const [warnings, setWarnings] = useState<MachineFaultDisplay[]>([]);

	const machines = useMemo(() => {
		if (currentMachineGroup === null) {
			return [];
		}

		return Object.entries(currentMachineGroup.configuredMachinesWithMachineType ?? {}).map(
			([machineId, machineType]) => ({
				machineId,
				machineType,
			}),
		);
	}, [currentMachineGroup]);

	useEffect(() => {
		const machineIds = new Set(machines.map(({ machineId }) => machineId));

		setErrors((currentErrors) => currentErrors.filter(({ machineId }) => machineIds.has(machineId)));
		setWarnings((currentWarnings) => currentWarnings.filter(({ machineId }) => machineIds.has(machineId)));
	}, [machines]);

	const updateErrors = useCallback((machineId: string, updatedErrors: MachineFaultDisplay[]) => {
		setErrors((oldErrors) => oldErrors.filter((fault) => fault.machineId !== machineId).concat(updatedErrors));
	}, []);

	const updateWarnings = useCallback((machineId: string, updatedWarnings: MachineFaultDisplay[]) => {
		setWarnings((oldWarnings) => oldWarnings.filter((fault) => fault.machineId !== machineId).concat(updatedWarnings));
	}, []);

	const openModal = useCallback(() => {
		setIsOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setIsOpen(false);
	}, []);

	const value = useMemo(
		() => ({
			isOpen,
			openModal,
			closeModal,
			faults: [...errors, ...warnings],
		}),
		[closeModal, errors, isOpen, openModal, warnings],
	);

	const machineTypes = new Set(machines.map(({ machineType }) => machineType));

	return (
		<ErrorsAndWarningsContext.Provider value={value}>
			{machineTypes.has("CVPRange") && (
				<CVPRangeErrorsAndWarnings
					machines={machines}
					onErrorsChanged={updateErrors}
					onWarningsChanged={updateWarnings}
				/>
			)}
			{machineTypes.has("Em") && <EmErrorsAndWarnings machines={machines} onErrorsChanged={updateErrors} />}
			{machineTypes.has("Fusion") && <FusionErrorsAndWarnings machines={machines} onErrorsChanged={updateErrors} />}
			{machineTypes.has("X4") && (
				<X4ErrorsAndWarnings machines={machines} onErrorsChanged={updateErrors} onWarningsChanged={updateWarnings} />
			)}
			{(machineTypes.has("X5") || machineTypes.has("X5wPrint")) && (
				<X5ErrorsAndWarnings machines={machines} onErrorsChanged={updateErrors} onWarningsChanged={updateWarnings} />
			)}
			{machineTypes.has("X6") && (
				<X6ErrorsAndWarnings machines={machines} onErrorsChanged={updateErrors} onWarningsChanged={updateWarnings} />
			)}
			{machineTypes.has("X7") && (
				<X7ErrorsAndWarnings machines={machines} onErrorsChanged={updateErrors} onWarningsChanged={updateWarnings} />
			)}
			{machineTypes.has("ZebraPrinter") && (
				<ZebraErrorsAndWarnings machines={machines} onErrorsChanged={updateErrors} />
			)}
			{children}
		</ErrorsAndWarningsContext.Provider>
	);
};
